import { useMemo } from 'react';
import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { TicketedEvent } from '../types/ticketedEvents';
import { stableStringify } from '@Utils/searchParams';

type TicketedEventsResponse =
  | TicketedEvent[]
  | { data: TicketedEvent[]; meta: Record<string, unknown> };
type DefaultError = unknown;

type UseTicketedEventsParams<
  S extends Record<string, unknown> = Record<string, unknown>,
> = {
  /** Optional query string params to forward to service.getTicketedEvents */
  searchParams?: S;
  /** Override default enabled behavior */
  enabled?: boolean;

  // Legacy params for backward compatibility
  pageNumber?: number;
  pageSize?: number;
  include?: string;
};

/**
 * useTicketedEvents
 *
 * @example
 * const eventsQuery = useTicketedEvents(
 *   {
 *     searchParams: {
 *       'page[number]': 1,
 *       'page[size]': 10,
 *       include: 'venue,featuredImage,images',
 *     },
 *   },
 *   {
 *     staleTime: 60_000,
 *   },
 * );
 */
export function useTicketedEvents<
  TData = TicketedEventsResponse,
  TError = DefaultError,
>(
  { searchParams, enabled = true }: UseTicketedEventsParams = {},
  options?: Omit<
    UseQueryOptions<TicketedEventsResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const stableParams = useMemo(
    () => stableStringify(searchParams),
    [searchParams],
  );

  const queryKey: QueryKey = ['ticketed-events', stableParams];

  return useQuery<TicketedEventsResponse, TError, TData>({
    queryKey,
    enabled,
    queryFn: async () => service.getTicketedEvents(searchParams ?? {}),
    ...(options as any),
  });
}
